// SETTINGS V2 atom — SInputField. The shared-wrapper adapter and the ONLY
// sanctioned text-input path in settings/v2. Composes SField chrome around the
// shared FormTextField (shared/components/redesign/forms) — NEVER a native
// <input>, NEVER raw MUI authored here (MUI *inside* the shared wrapper is
// sanctioned per SHARED_DEPLOY_AND_RULES canonical decision 1/3).
//
// SP0 acceptance = adapter compiles + renders. Pixel reconciliation (bg-soft,
// mod-accent focus border + ring, disabled affordance) is reconciled in SP2 via
// `visual="cssVars"` — achieved via FormTextField props, never `sx`
// (grep-banned in v2) and never inline MUI. The `type` union needed
// "password"/"tel", so FormTextField was extended additively (logged in
// BREAKING_CHANGES.md); SP2 additively adds `visual` + `endAdornment` there too.
import React from "react";
import FormTextField from "../../../../shared/components/redesign/forms/FormTextField";
import SField from "./SField";

export interface SInputFieldProps {
  label: string;
  required?: boolean;
  error?: string;
  unit?: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  type?: "text" | "password" | "tel" | "number" | "time";
  mono?: boolean;
  /** Optional trailing control (e.g. password visibility eye button, SP2). */
  endAdornment?: React.ReactNode;
  /** Character cap, passed through to FormTextField's `maxLength` (SP4 — the
   *  location name field's 200-char parity). */
  maxLength?: number;
  /** Native <input> attribute pass-through to FormTextField's `inputProps`
   *  (SP7 — carries `{min: 0, step: 0.01}` for the pricing rate fields). Merged
   *  with the mono font-family style below; caller keys win. */
  inputProps?: Record<string, unknown>;
}

const SInputField: React.FC<SInputFieldProps> = ({
  label,
  required,
  error,
  unit,
  value,
  onChange,
  placeholder,
  disabled,
  type = "text",
  mono,
  endAdornment,
  maxLength,
  inputProps,
}) => (
  <SField label={label} required={required} error={error} unit={unit}>
    <FormTextField
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      type={type}
      error={!!error}
      fullWidth
      visual="cssVars"
      endAdornment={endAdornment}
      maxLength={maxLength}
      inputProps={{
        style: { fontFamily: mono ? "var(--font-mono)" : "inherit" },
        ...inputProps,
      }}
    />
  </SField>
);

export default SInputField;
